import { useMemo, useRef } from 'react'
import type { ElevationProfile } from '../lib/elevation'
import { formatKm } from '../lib/geo'

interface Props {
  profile: ElevationProfile
  onHover?: (atM: number | null) => void
}

const W = 720
const H = 240
const PAD = { l: 6, r: 6, t: 12, b: 6 }

/** gradient % → color, Komoot-style steepness tint */
function gradeColor(pct: number): string {
  const a = Math.abs(pct)
  if (a < 4) return '#74b56b'
  if (a < 8) return '#e0a13c'
  if (a < 13) return '#e07b3c'
  return '#d64545'
}

export default function ElevationChart({ profile, onHover }: Props) {
  const svgRef = useRef<SVGSVGElement>(null)
  const { samples, minEleM, maxEleM } = profile
  const totalM = samples[samples.length - 1][0]

  const { areaPath, segments } = useMemo(() => {
    const span = Math.max(40, maxEleM - minEleM)
    const x = (d: number) => PAD.l + ((W - PAD.l - PAD.r) * d) / totalM
    const y = (e: number) => PAD.t + (H - PAD.t - PAD.b) * (1 - (e - minEleM + (span - (maxEleM - minEleM)) / 2) / span)

    let d = `M ${x(samples[0][0])} ${H - PAD.b}`
    for (const [dist, ele] of samples) d += ` L ${x(dist).toFixed(1)} ${y(ele).toFixed(1)}`
    d += ` L ${x(totalM)} ${H - PAD.b} Z`

    const segs: { d: string; color: string }[] = []
    for (let i = 1; i < samples.length; i++) {
      const [d0, e0] = samples[i - 1]
      const [d1, e1] = samples[i]
      const pct = d1 - d0 > 0 ? ((e1 - e0) / (d1 - d0)) * 100 : 0
      segs.push({
        d: `M ${x(d0).toFixed(1)} ${y(e0).toFixed(1)} L ${x(d1).toFixed(1)} ${y(e1).toFixed(1)}`,
        color: gradeColor(pct),
      })
    }
    return { areaPath: d, segments: segs }
  }, [samples, minEleM, maxEleM, totalM])

  const handleMove = (clientX: number) => {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect || !onHover) return
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    onHover(frac * totalM)
  }

  return (
    <div className="elev-wrap">
      <svg
        ref={svgRef}
        className="elev-chart"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        onMouseMove={(e) => handleMove(e.clientX)}
        onMouseLeave={() => onHover?.(null)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
        onTouchEnd={() => onHover?.(null)}
      >
        <path d={areaPath} fill="#e3f2e8" />
        {segments.map((s, i) => (
          <path key={i} d={s.d} stroke={s.color} strokeWidth={3} fill="none" strokeLinecap="round" />
        ))}
      </svg>
      <div className="elev-meta">
        <span>{formatKm(0)}</span>
        <span>
          {minEleM} – {maxEleM} m
        </span>
        <span>{formatKm(totalM)}</span>
      </div>
    </div>
  )
}
